/**
 * BrowserCallProvider (Phase 6 item #53).
 *
 * Singleton Twilio Voice SDK Device wrapper for in-browser calling. Lifted
 * from the Patriot Systems canonical pattern: one Device per page, lazy
 * register, 50-minute token refresh ring (Twilio Voice SDK#93 bug
 * workaround), React StrictMode safe.
 *
 * SDK import note: @twilio/voice-sdk is loaded dynamically so the Phase 6
 * UI can ship without the SDK installed; when an operator wants browser
 * calling they install the package and add Twilio creds. Until then this
 * file exports a stub that surfaces a clear "not configured" state to
 * callers (the ActiveCallBanner and any future click-to-call buttons).
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { Device as TwilioDevice, Call as TwilioCall } from '@twilio/voice-sdk';
import { api } from '../lib/api.js';

export type BrowserCallState =
  | { status: 'idle' }
  | { status: 'initializing' }
  | { status: 'ready' }
  | { status: 'dialing'; toNumber: string }
  | { status: 'on-call'; toNumber: string; startedAt: number }
  | { status: 'error'; reason: string };

interface BrowserCallContextValue {
  state: BrowserCallState;
  /** True when the underlying SDK + creds are configured (else stub mode). */
  available: boolean;
  /** Place an outbound call. No-op + sets error state when unavailable. */
  dial: (toNumber: string, options?: { identityId?: string; overrideIdentityId?: string; dealThreadId?: string; partyId?: string | null; tenantId?: string }) => Promise<void>;
  /** End the active call. No-op when not on a call. */
  hangup: () => void;
  /** True when the active call mic is muted. */
  muted: boolean;
  /** Mute/unmute the active call mic. Toggles when no arg is given. */
  mute: (next?: boolean) => void;
}

const BrowserCallContext = createContext<BrowserCallContextValue | null>(null);

export function useBrowserCall(): BrowserCallContextValue {
  const ctx = useContext(BrowserCallContext);
  if (!ctx) {
    // Outside of provider - return a permanent stub so callers don't crash
    return {
      state: { status: 'idle' },
      available: false,
      dial: async () => { /* stub */ },
      hangup: () => { /* stub */ },
      muted: false,
      mute: () => { /* stub */ },
    };
  }
  return ctx;
}

interface TokenResponse {
  token?: string;
  available: boolean;
}

export default function BrowserCallProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<BrowserCallState>({ status: 'idle' });
  const [available, setAvailable] = useState(false);
  const [muted, setMuted] = useState(false);
  const deviceRef = useRef<TwilioDevice | null>(null);
  const callRef = useRef<TwilioCall | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Probe availability on mount. The server responds with available=true
  // when TWILIO_ACCOUNT_SID + TWILIO_API_KEY/SECRET + TWIML_APP_SID are set.
  useEffect(() => {
    let cancelled = false;
    api.get<TokenResponse>('/api/deal-calls/twilio-token')
      .then((res) => {
        if (cancelled) return;
        setAvailable(Boolean(res.available));
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      });
    return () => { cancelled = true; };
  }, []);

  const fetchToken = useCallback(async (): Promise<string | null> => {
    try {
      const res = await api.get<TokenResponse>('/api/deal-calls/twilio-token');
      return res.token ?? null;
    } catch {
      return null;
    }
  }, []);

  // Token refresh ring - 50 min interval beats the 1-hour token expiry and
  // avoids the SDK#93 race where reconnect on expired token loses callbacks.
  useEffect(() => {
    if (!available) return;
    const id = setInterval(async () => {
      const token = await fetchToken();
      if (token && deviceRef.current) {
        try { deviceRef.current.updateToken(token); } catch { /* swallow */ }
      }
    }, 50 * 60 * 1000);
    refreshTimerRef.current = id;
    return () => {
      clearInterval(id);
      refreshTimerRef.current = null;
    };
  }, [available, fetchToken]);

  // Tear the Device down on unmount so a hot reload / route change doesn't
  // leak a registered Device or a live call.
  useEffect(() => {
    return () => {
      try { callRef.current?.disconnect(); } catch { /* noop */ }
      try { deviceRef.current?.destroy(); } catch { /* noop */ }
      callRef.current = null;
      deviceRef.current = null;
    };
  }, []);

  // Lazily create the Twilio Device on first dial (the SDK is browser-only and
  // loaded dynamically so it never weighs on the initial bundle).
  const ensureDevice = useCallback(async (): Promise<TwilioDevice | null> => {
    if (deviceRef.current) return deviceRef.current;
    const token = await fetchToken();
    if (!token) return null;
    const { Device } = await import('@twilio/voice-sdk');
    const device = new Device(token, { logLevel: 'error' } as never);
    device.on('error', (err: { message?: string }) => {
      setState({ status: 'error', reason: err?.message || 'Calling device error' });
    });
    deviceRef.current = device;
    return device;
  }, [fetchToken]);

  const dial = useCallback(async (
    toNumber: string,
    options?: { identityId?: string; overrideIdentityId?: string; dealThreadId?: string; partyId?: string | null; tenantId?: string },
  ) => {
    if (!available) {
      setState({ status: 'error', reason: 'Browser calling is not configured. Connect a Twilio number in Settings.' });
      return;
    }
    setState({ status: 'dialing', toNumber });
    setMuted(false);
    try {
      const device = await ensureDevice();
      if (!device) {
        setState({ status: 'error', reason: 'Could not get a calling token. Check your connection and try again.' });
        return;
      }
      const call = await device.connect({
        params: {
          To: toNumber,
          ...(options?.identityId ? { IdentityId: options.identityId } : {}),
          ...(options?.overrideIdentityId ? { OverrideIdentityId: options.overrideIdentityId } : {}),
          ...(options?.dealThreadId ? { DealThreadId: options.dealThreadId } : {}),
          ...(options?.partyId ? { PartyId: options.partyId } : {}),
          ...(options?.tenantId ? { TenantId: options.tenantId } : {}),
        },
      });
      callRef.current = call;
      call.on('accept', () => setState({ status: 'on-call', toNumber, startedAt: Date.now() }));
      const end = () => { callRef.current = null; setMuted(false); setState({ status: 'ready' }); };
      call.on('disconnect', end);
      call.on('cancel', end);
      call.on('reject', end);
      call.on('error', (err: { message?: string }) =>
        setState({ status: 'error', reason: err?.message || 'Call failed' }));
    } catch (err) {
      callRef.current = null;
      setState({ status: 'error', reason: (err as Error)?.message || 'Call failed to start' });
    }
  }, [available, ensureDevice]);

  const hangup = useCallback(() => {
    try { callRef.current?.disconnect(); } catch { /* noop */ }
    try { deviceRef.current?.disconnectAll(); } catch { /* noop */ }
    callRef.current = null;
    setMuted(false);
    setState({ status: 'idle' });
  }, []);

  // Mute/unmute the active call mic. Toggles when no arg is given.
  const mute = useCallback((next?: boolean) => {
    const target = typeof next === 'boolean' ? next : !muted;
    try { callRef.current?.mute(target); } catch { /* noop */ }
    setMuted(target);
  }, [muted]);

  return (
    <BrowserCallContext.Provider value={{ state, available, dial, hangup, muted, mute }}>
      {children}
    </BrowserCallContext.Provider>
  );
}
